import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useRazorpay } from '../hooks/useRazorpay';

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 10,
  border: '1.5px solid rgba(74,124,89,0.15)', background: '#FFFEF9',
  fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#2D3B2D',
  outline: 'none', transition: 'border-color 0.3s',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#4A5A4A', marginBottom: 5, letterSpacing: 0.3,
};

const indianStates = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
  'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir',
  'Ladakh','Chandigarh','Puducherry',
];

export default function CheckoutPage({ onBack, onSuccess }) {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const { initiatePayment, loading, error } = useRazorpay();
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [orderPlaced, setOrderPlaced] = useState(null);

  const [address, setAddress] = useState({
    name: '', phone: '', street: '', city: '', state: '', pincode: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const updateField = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!address.name.trim()) errors.name = 'Name is required';
    if (!address.phone.trim()) errors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(address.phone)) errors.phone = 'Enter a valid 10-digit Indian number';
    if (!address.street.trim()) errors.street = 'Address is required';
    if (!address.city.trim()) errors.city = 'City is required';
    if (!address.state) errors.state = 'State is required';
    if (!address.pincode.trim()) errors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(address.pincode)) errors.pincode = 'Enter a valid 6-digit pincode';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    if (paymentMethod === 'razorpay') {
      const result = await initiatePayment({
        items,
        shippingAddress: address,
        userInfo: { name: address.name, phone: address.phone },
      });

      if (result.success) {
        setOrderPlaced(result.order);
        clearCart();
      }
    }
    // COD flow would call /api/v1/orders directly
  };

  // ── Success Screen ──
  if (orderPlaced) {
    return (
      <div style={{
        minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, animation: 'fu 0.5s ease-out',
      }}>
        <style>{`@keyframes fu{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}`}</style>
        <div style={{
          background: '#FFFEF9', borderRadius: 20, padding: '48px 40px', textAlign: 'center',
          maxWidth: 480, width: '100%',
          boxShadow: '0 12px 40px rgba(45,59,45,0.1)',
          border: '1px solid rgba(74,124,89,0.08)',
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #4A7C59, #2D5A3F)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 36,
          }}>✓</div>
          <h2 style={{
            fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700,
            color: '#2D3B2D', marginBottom: 8,
          }}>Order Confirmed!</h2>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: 17, color: '#5A6B5A',
            lineHeight: 1.6, marginBottom: 24,
          }}>
            Thank you for choosing Gramaayam. Your organic products are being prepared with love.
          </p>
          <div style={{
            background: '#FAF8F2', borderRadius: 12, padding: '16px 20px', marginBottom: 24,
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#7A8B6F' }}>Order Number</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#2D5A3F', fontFamily: 'monospace' }}>
                {orderPlaced.orderNumber}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#7A8B6F' }}>Total Paid</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#2D5A3F' }}>
                ₹{orderPlaced.totalAmount}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#7A8B6F' }}>Status</span>
              <span style={{
                fontSize: 12, fontWeight: 600, color: '#4A7C59',
                background: 'rgba(74,124,89,0.1)', padding: '2px 10px', borderRadius: 10,
              }}>
                {orderPlaced.orderStatus === 'confirmed' ? 'Confirmed' : 'Placed'}
              </span>
            </div>
          </div>
          <button onClick={onBack} style={{
            background: 'linear-gradient(135deg, #2D5A3F, #1E3F2B)', color: '#F5F0E8',
            border: 'none', padding: '13px 32px', borderRadius: 28, fontSize: 14,
            fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 16px rgba(45,90,63,0.25)',
          }}>Continue Shopping</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '100px 20px 60px' }}>
      <style>{`@keyframes fu{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', fontSize: 14, color: '#4A7C59',
        fontWeight: 600, marginBottom: 20, cursor: 'pointer', display: 'flex',
        alignItems: 'center', gap: 6,
      }}>← Back to Cart</button>

      <h1 style={{
        fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700,
        color: '#2D3B2D', marginBottom: 32,
      }}>Checkout</h1>

      <div style={{
        display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 32, alignItems: 'start',
      }}>
        {/* ── Left: Address + Payment ── */}
        <div style={{ animation: 'fu 0.4s ease-out' }}>
          {/* Shipping Address */}
          <div style={{
            background: '#FFFEF9', borderRadius: 16, padding: 28,
            border: '1px solid rgba(74,124,89,0.08)', marginBottom: 24,
          }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700,
              color: '#2D3B2D', marginBottom: 20,
            }}>📦 Shipping Address</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input style={{...inputStyle, borderColor: formErrors.name ? '#E85D3A' : undefined}}
                  placeholder="Ramesh Kumar" value={address.name}
                  onChange={(e) => updateField('name', e.target.value)} />
                {formErrors.name && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.name}</p>}
              </div>
              <div>
                <label style={labelStyle}>Phone Number *</label>
                <input style={{...inputStyle, borderColor: formErrors.phone ? '#E85D3A' : undefined}}
                  placeholder="9876543210" value={address.phone}
                  onChange={(e) => updateField('phone', e.target.value)} maxLength={10} />
                {formErrors.phone && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.phone}</p>}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Street Address *</label>
                <input style={{...inputStyle, borderColor: formErrors.street ? '#E85D3A' : undefined}}
                  placeholder="123, Gali No. 4, Near Temple" value={address.street}
                  onChange={(e) => updateField('street', e.target.value)} />
                {formErrors.street && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.street}</p>}
              </div>
              <div>
                <label style={labelStyle}>City *</label>
                <input style={{...inputStyle, borderColor: formErrors.city ? '#E85D3A' : undefined}}
                  placeholder="Varanasi" value={address.city}
                  onChange={(e) => updateField('city', e.target.value)} />
                {formErrors.city && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.city}</p>}
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <select style={{...inputStyle, borderColor: formErrors.state ? '#E85D3A' : undefined}}
                  value={address.state} onChange={(e) => updateField('state', e.target.value)}>
                  <option value="">Select State</option>
                  {indianStates.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {formErrors.state && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.state}</p>}
              </div>
              <div>
                <label style={labelStyle}>Pincode *</label>
                <input style={{...inputStyle, borderColor: formErrors.pincode ? '#E85D3A' : undefined}}
                  placeholder="221001" value={address.pincode}
                  onChange={(e) => updateField('pincode', e.target.value)} maxLength={6} />
                {formErrors.pincode && <p style={{ color:'#E85D3A', fontSize:11, marginTop:3 }}>{formErrors.pincode}</p>}
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div style={{
            background: '#FFFEF9', borderRadius: 16, padding: 28,
            border: '1px solid rgba(74,124,89,0.08)',
          }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700,
              color: '#2D3B2D', marginBottom: 20,
            }}>💳 Payment Method</h3>

            {[
              {
                id: 'razorpay', label: 'Pay Online', icon: '🔒',
                desc: 'UPI, Cards, Net Banking, Wallets — Secured by Razorpay',
              },
              {
                id: 'cod', label: 'Cash on Delivery', icon: '💵',
                desc: 'Pay when you receive your order (₹30 extra COD charge)',
              },
            ].map((method) => (
              <label key={method.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14, padding: 16,
                borderRadius: 12, cursor: 'pointer', marginBottom: 10,
                border: paymentMethod === method.id
                  ? '2px solid #2D5A3F'
                  : '2px solid rgba(74,124,89,0.1)',
                background: paymentMethod === method.id ? 'rgba(45,90,63,0.03)' : 'transparent',
                transition: 'all 0.3s',
              }}>
                <input
                  type="radio" name="payment" value={method.id}
                  checked={paymentMethod === method.id}
                  onChange={() => setPaymentMethod(method.id)}
                  style={{ marginTop: 4, accentColor: '#2D5A3F' }}
                />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#2D3B2D' }}>
                    {method.icon} {method.label}
                  </div>
                  <div style={{ fontSize: 12, color: '#7A8B6F', marginTop: 3 }}>{method.desc}</div>
                  {method.id === 'razorpay' && paymentMethod === 'razorpay' && (
                    <div style={{
                      display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap',
                    }}>
                      {['UPI', 'Visa', 'Mastercard', 'RuPay', 'Net Banking', 'Paytm'].map((m) => (
                        <span key={m} style={{
                          fontSize: 10, padding: '3px 8px', borderRadius: 6,
                          background: 'rgba(74,124,89,0.08)', color: '#4A7C59',
                          fontWeight: 600,
                        }}>{m}</span>
                      ))}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* ── Right: Order Summary ── */}
        <div style={{
          background: '#FFFEF9', borderRadius: 16, padding: 28,
          border: '1px solid rgba(74,124,89,0.08)',
          position: 'sticky', top: 90, animation: 'fu 0.5s ease-out 0.1s both',
        }}>
          <h3 style={{
            fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700,
            color: '#2D3B2D', marginBottom: 20,
          }}>🛒 Order Summary</h3>

          {items.map((item) => (
            <div key={item._id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0', borderBottom: '1px solid rgba(74,124,89,0.06)',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#2D3B2D' }}>{item.name}</div>
                <div style={{ fontSize: 11, color: '#7A8B6F' }}>Qty: {item.qty}</div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#2D5A3F' }}>
                ₹{item.price * item.qty}
              </span>
            </div>
          ))}

          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#7A8B6F' }}>Subtotal</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>₹{subtotal}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: '#7A8B6F' }}>Shipping</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: shipping === 0 ? '#4A7C59' : '#2D3B2D' }}>
                {shipping === 0 ? 'FREE' : `₹${shipping}`}
              </span>
            </div>
            {paymentMethod === 'cod' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#7A8B6F' }}>COD Charge</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>₹30</span>
              </div>
            )}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '14px 0', borderTop: '2px solid rgba(74,124,89,0.1)',
              marginTop: 8,
            }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>Total</span>
              <span style={{
                fontFamily: "'Playfair Display', serif", fontSize: 24,
                fontWeight: 700, color: '#2D5A3F',
              }}>₹{total + (paymentMethod === 'cod' ? 30 : 0)}</span>
            </div>
          </div>

          {error && (
            <div style={{
              background: 'rgba(232,93,58,0.08)', color: '#C1440E', padding: '10px 14px',
              borderRadius: 10, fontSize: 13, marginTop: 12, marginBottom: 4,
            }}>
              ⚠️ {error}
            </div>
          )}

          <button
            onClick={handlePayment}
            disabled={loading || items.length === 0}
            style={{
              width: '100%', marginTop: 16, padding: 15, borderRadius: 28,
              background: loading
                ? '#A8B89C'
                : 'linear-gradient(135deg, #2D5A3F, #1E3F2B)',
              color: '#F5F0E8', border: 'none', fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(45,90,63,0.25)',
              transition: 'all 0.3s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {loading ? (
              <>
                <span style={{
                  width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite', display: 'inline-block',
                }} />
                Processing...
              </>
            ) : paymentMethod === 'razorpay' ? (
              <>🔒 Pay ₹{total} Securely</>
            ) : (
              <>📦 Place Order (COD)</>
            )}
          </button>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, marginTop: 14,
          }}>
            <span style={{ fontSize: 12 }}>🔒</span>
            <span style={{ fontSize: 11, color: '#A8B89C' }}>
              Secured by Razorpay • 256-bit SSL encryption
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
